import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { participantLabel, labelFor, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { IconButton } from '@/components/common/IconButton';
import { Plus, Download, FileText, Eye, Search, Receipt, DollarSign, AlertCircle, Filter, X, FileSpreadsheet, FileDown, Loader2, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { useAuth } from '@/lib/auth';
import { formatInvoiceQuantity } from '@/lib/invoiceQuantity';
import { getBrandName } from '@/lib/brandName';

const STATUS_FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'GENERATED', label: 'Generated' },
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'PAID', label: 'Paid' },
  { key: 'VOIDED', label: 'Voided' },
];

const PERIOD_OPTIONS = [
  { key: 'all', label: 'All Time' },
  { key: 'this_week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom', label: 'Custom Range' },
];

const fmtAU = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return day ? `${day}/${m}/${y}` : d;
};
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// First per-participant reason a shared (1:N) shift could not be split, e.g. "NO_SERVICE_ITEM: no active …".
function describeFanoutWarning(w) {
  const b = (w?.blocked || [])[0];
  return b ? `${b.code}: ${b.message}` : (w?.message || '');
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [participantFilter, setParticipantFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [branches, setBranches] = useState([]);
  const [period, setPeriod] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [appliedRange, setAppliedRange] = useState({ start: '', end: '' });
  const [customError, setCustomError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ count: 0, total: 0, paid: 0, outstanding: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const { hasPermission } = useAuth();
  const canVoid = hasPermission('invoices.manage');
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidProcessing, setVoidProcessing] = useState(false);

  const handleVoidInvoice = async () => {
    if (!voidTarget || !voidReason.trim() || voidProcessing) return;
    setVoidProcessing(true);
    try {
      const res = await api.post(`/invoices/${voidTarget.id}/void`, { reason: voidReason.trim() });
      const d = res.data?.data || {};
      if (d.already_voided) {
        toast.info('Invoice was already voided — no funding change');
      } else {
        toast.success(`Invoice voided · ${money(d.restored_amount)} funding restored`);
      }
      setVoidTarget(null); setVoidReason('');
      setDetailDialog(null);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || 'Failed to void invoice');
    } finally {
      setVoidProcessing(false);
    }
  };
  const [generateConfirm, setGenerateConfirm] = useState(false);
  const [form, setForm] = useState({ participant_id: '', period_start: '', period_end: '' });
  const [loading, setLoading] = useState(false);   // generation
  const [listLoading, setListLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Build the shared filter query string for list + export
  const buildParams = useCallback((extra = {}) => {
    const qp = new URLSearchParams();
    if (debouncedSearch) qp.set('search', debouncedSearch);
    if (statusFilter && statusFilter !== 'ALL') qp.set('status', statusFilter);
    if (participantFilter) qp.set('participant_id', participantFilter);
    if (branchFilter) qp.set('branch_id', branchFilter);
    if (period && period !== 'all') qp.set('period', period);
    if (period === 'custom' && appliedRange.start && appliedRange.end) {
      qp.set('start_date', appliedRange.start);
      qp.set('end_date', appliedRange.end);
    }
    Object.entries(extra).forEach(([k, v]) => qp.set(k, v));
    return qp;
  }, [debouncedSearch, statusFilter, participantFilter, branchFilter, period, appliedRange]);

  const fetchData = useCallback(async () => {
    // For custom period, wait until a valid range is applied
    if (period === 'custom' && !(appliedRange.start && appliedRange.end)) {
      setListLoading(false);
      return;
    }
    setListLoading(true);
    setLoadError(false);
    try {
      const qp = buildParams({ page: String(page), pageSize: String(pageSize) });
      const invRes = await api.get(`/invoices?${qp.toString()}`);
      setInvoices(invRes.data.items || []);
      setTotal(invRes.data.total || 0);
      setSummary(invRes.data.summary || { count: 0, total: 0, paid: 0, outstanding: 0 });
    } catch (e) {
      setLoadError(true);
      notify.error(e, 'INVOICE_LOAD_FAILED');
    } finally {
      setListLoading(false);
    }
  }, [buildParams, page, pageSize, period, appliedRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    api.get('/participants?pageSize=100').then(r => setParticipants(r.data.items || r.data.data || [])).catch(() => {});
    api.get('/org/branches').then(r => setBranches(r.data.data || r.data.items || [])).catch(() => {});
  }, []);

  // Debounce free-text search; reset to page 1
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const applyCustomRange = () => {
    if (!customStart || !customEnd) { setCustomError('Both a start date and an end date are required.'); return; }
    if (customEnd < customStart) { setCustomError('End date cannot be earlier than start date.'); return; }
    setCustomError('');
    setAppliedRange({ start: customStart, end: customEnd });
    setPage(1);
  };
  const clearCustomRange = () => {
    setCustomStart(''); setCustomEnd(''); setCustomError('');
    setAppliedRange({ start: '', end: '' });
    setPeriod('all'); setPage(1);
  };

  const onPeriodChange = (v) => {
    setPeriod(v);
    setPage(1);
    if (v !== 'custom') { setAppliedRange({ start: '', end: '' }); setCustomError(''); }
  };

  const doExport = async (format) => {
    if (period === 'custom' && !(appliedRange.start && appliedRange.end)) {
      toast.error('Apply a valid custom date range before exporting.');
      return;
    }
    setExporting(true);
    try {
      const qp = buildParams({ format });
      const res = await api.get(`/invoices/export?${qp.toString()}`, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `${getBrandName().replace(/[^A-Za-z0-9]+/g, '_')}_Invoices.${format === 'excel' ? 'xlsx' : format}`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export ready');
    } catch (e) {
      notify.error(e, 'INVOICE_EXPORT_FAILED');
    } finally {
      setExporting(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await api.post('/invoices/generate', form);
      const inv = res?.data?.data || {};
      const splitSummary = inv.auto_split_summary;
      if (inv.is_split_billed && splitSummary?.lines > 1) {
        toast.success(`Invoice ${inv.invoice_number} generated · Split-billed (auto) · ${splitSummary.lines} lines across ${splitSummary.shifts} shift${splitSummary.shifts > 1 ? 's' : ''}`, { duration: 6000 });
      } else {
        toast.success(`Invoice ${inv.invoice_number || ''} generated`.trim());
      }
      const billingBlocked = inv.legacy_billing_blocked || [];
      if (billingBlocked.length > 0) {
        const noRate = billingBlocked.filter(b => b.code === 'NO_RATE');
        toast.warning(noRate.length === billingBlocked.length
          ? `${noRate.length} timesheet(s) not invoiced — the service has no rate set`
          : `${billingBlocked.length} timesheet(s) not invoiced`, {
          description: billingBlocked[0]?.message, duration: 12000,
        });
      }
      const routingBlocked = inv.funding_routing_blocked || [];
      if (routingBlocked.length > 0) {
        toast.warning(`${routingBlocked.length} timesheet(s) not invoiced — funding routing`, {
          description: routingBlocked[0]?.message, duration: 8000,
        });
      }
      const fanoutWarnings = res?.data?.fanout_warnings || [];
      if (fanoutWarnings.length > 0) {
        toast.warning(`${fanoutWarnings.length} shared shift(s) could not be split to this participant`, {
          description: describeFanoutWarning(fanoutWarnings[0]), duration: 10000,
        });
      }
      setGenerateConfirm(false); setDialogOpen(false);
      setForm({ participant_id: '', period_start: '', period_end: '' });
      setPage(1); fetchData();
    } catch (e) {
      // 409 = nothing billable: show the backend's reasons instead of the generic failure.
      const err = e?.response?.status === 409 ? e?.response?.data?.error : null;
      if (err?.message) {
        const d = err.details || {};
        const blocked = [...(d.legacy_billing_blocked || []), ...(d.funding_routing_blocked || []),
          ...(d.per_participant_billing_blocked || []), ...(d.readiness_blocked || [])];
        const lines = blocked.slice(0, 3).map(b => `${b.date || ''} ${b.message || b.code || b.error || b.reason}`.trim());
        if ((d.fanout_warnings || []).length) lines.push(`Shared shift: ${describeFanoutWarning(d.fanout_warnings[0])}`);
        toast.error(err.message, { description: lines.join(' · ') || undefined, duration: 12000 });
      } else {
        notify.error(e, 'INVOICE_GENERATE_FAILED');
      }
    }
    finally { setLoading(false); }
  };

  const viewDetail = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}`);
      const detail = res.data.data;
      let allocationLines = null;
      if (detail?.is_split_billed) {
        try { const alloc = await api.get(`/invoices/${inv.id}/allocation-lines`); allocationLines = alloc.data?.data; }
        catch { allocationLines = null; }
      }
      setDetailDialog({ ...detail, _allocation_lines: allocationLines });
    } catch (e) { notify.error(e, 'INVOICE_DETAIL_LOAD_FAILED'); }
  };

  const downloadRowPDF = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/export-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `invoice-${id}.pdf`; a.click();
      URL.revokeObjectURL(url); toast.success('Invoice downloaded');
    } catch (e) { notify.error(e, 'INVOICE_PDF_EXPORT_FAILED'); }
  };

  const hasActiveFilters = search || statusFilter !== 'ALL' || participantFilter || branchFilter || period !== 'all';
  const clearAllFilters = () => {
    setSearch(''); setStatusFilter('ALL'); setParticipantFilter(''); setBranchFilter('');
    setPeriod('all'); setAppliedRange({ start: '', end: '' }); setCustomStart(''); setCustomEnd(''); setCustomError(''); setPage(1);
  };

  const periodLabel = (() => {
    if (period === 'custom' && appliedRange.start && appliedRange.end) return `${fmtAU(appliedRange.start)} – ${fmtAU(appliedRange.end)}`;
    return PERIOD_OPTIONS.find(p => p.key === period)?.label || 'All Time';
  })();

  const isEmpty = !listLoading && !loadError && invoices.length === 0;

  return (
    <div className="space-y-5 p-1 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">Generate, filter and export NDIS invoices from approved timesheets.</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting} data-testid="invoices-export-button">
                {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                {exporting ? 'Preparing export…' : 'Export'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => doExport('csv')} data-testid="invoices-export-csv"><FileText className="h-3.5 w-3.5 mr-2" /> Export CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport('excel')} data-testid="invoices-export-excel"><FileSpreadsheet className="h-3.5 w-3.5 mr-2" /> Export Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport('pdf')} data-testid="invoices-export-pdf"><FileDown className="h-3.5 w-3.5 mr-2" /> Export PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invoice-generate-button"><Plus className="h-4 w-4 mr-2" /> Generate Invoice</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Generate Invoice</DialogTitle><DialogDescription>Generate from approved, un-invoiced timesheets</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Participant</Label>
                  <AsyncSearchSelect
                    endpoint="/participants"
                    value={form.participant_id}
                    onChange={(v) => setForm({ ...form, participant_id: v })}
                    getOptionLabel={participantLabel}
                    selectedLabel={labelFor(participants, form.participant_id, participantLabel)}
                    placeholder="Select participant"
                    searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                    emptyText="No participants match that search"
                    testId="invoice-participant-select"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Period Start</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} required /></div>
                  <div className="space-y-2"><Label>Period End</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} required /></div>
                </div>
                <Button className="w-full" onClick={() => setGenerateConfirm(true)} disabled={!form.participant_id || !form.period_start || !form.period_end} data-testid="invoice-submit-button">Review & Generate</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary KPI band — from backend-filtered dataset */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="invoices-summary">
        <Card className="border-slate-200"><CardContent className="p-3.5">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><Receipt className="h-3.5 w-3.5" /> Total invoices</div>
          <div className="mt-1 text-xl font-bold tabular-nums text-slate-900" data-testid="invoices-summary-count">{summary.count}</div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3.5">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><DollarSign className="h-3.5 w-3.5" /> Total value</div>
          <div className="mt-1 text-xl font-bold tabular-nums text-slate-900" data-testid="invoices-summary-total">{money(summary.total)}</div>
        </CardContent></Card>
        <Card className="border-amber-200 bg-amber-50/40"><CardContent className="p-3.5">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-amber-700"><AlertCircle className="h-3.5 w-3.5" /> Outstanding</div>
          <div className="mt-1 text-xl font-bold tabular-nums text-amber-900" data-testid="invoices-summary-outstanding">{money(summary.outstanding)}</div>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50/40"><CardContent className="p-3.5">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700"><DollarSign className="h-3.5 w-3.5" /> Paid</div>
          <div className="mt-1 text-xl font-bold tabular-nums text-emerald-900" data-testid="invoices-summary-paid">{money(summary.paid)}</div>
        </CardContent></Card>
      </div>

      {/* Filter bar — row 1 */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:flex-wrap">
        <div className="relative w-full lg:w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search invoice # or participant…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="invoice-search" />
        </div>
        <div className="w-full lg:w-[180px]">
          <Select value={period} onValueChange={onPeriodChange}>
            <SelectTrigger data-testid="invoice-period-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIOD_OPTIONS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-full lg:w-[200px]">
          <AsyncSearchSelect
            endpoint="/participants"
            value={participantFilter}
            onChange={(v) => { setParticipantFilter(v || ''); setPage(1); }}
            getOptionLabel={participantLabel}
            selectedLabel={labelFor(participants, participantFilter, participantLabel)}
            placeholder="All participants"
            searchPlaceholder={PARTICIPANT_SEARCH_HINT}
            emptyText="No participants match that search"
            clearLabel="All participants"
            testId="invoice-participant-filter"
          />
        </div>
        {branches.length > 0 && (
          <div className="w-full lg:w-[180px]">
            <Select value={branchFilter || 'ALL'} onValueChange={(v) => { setBranchFilter(v === 'ALL' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="invoice-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id || b._id} value={b.id || b._id}>{b.name || b.branch_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Custom range row */}
      {period === 'custom' && (
        <div className="flex flex-col gap-2 rounded-md border border-violet-200 bg-violet-50/40 p-3" data-testid="invoice-custom-range">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="space-y-1"><Label className="text-xs">Start Date</Label><Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="invoice-custom-start" /></div>
            <div className="space-y-1"><Label className="text-xs">End Date</Label><Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="invoice-custom-end" /></div>
            <Button size="sm" onClick={applyCustomRange} data-testid="invoice-custom-apply">Apply</Button>
            <Button size="sm" variant="outline" onClick={clearCustomRange} data-testid="invoice-custom-clear">Clear</Button>
          </div>
          {customError && <p className="text-xs text-red-600" data-testid="invoice-custom-error">{customError}</p>}
        </div>
      )}

      {/* Status pills row */}
      <div className="flex items-center gap-2 overflow-x-auto" data-testid="invoice-status-filters">
        <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        {STATUS_FILTERS.map(s => (
          <button key={s.key} onClick={() => { setStatusFilter(s.key); setPage(1); }} data-testid={`invoice-status-filter-${s.key.toLowerCase()}`}
            className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${statusFilter === s.key ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
            {s.label}
          </button>
        ))}
        <span className="shrink-0 text-xs text-muted-foreground ml-1" data-testid="invoices-result-count">{total} result{total === 1 ? '' : 's'} · {periodLabel}</span>
        {hasActiveFilters && (
          <button onClick={clearAllFilters} data-testid="invoice-filters-clear" className="shrink-0 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 px-2 py-1">
            <X className="h-3 w-3" /> Clear Filters
          </button>
        )}
      </div>

      {/* List */}
      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          {listLoading ? (
            <TableSkeleton rows={6} columns={6} showHeader headers={['Invoice #', 'Participant', 'Period', 'Total', 'Status', 'Actions']} testid="invoices-loading" />
          ) : loadError ? (
            <EmptyState icon={AlertCircle} title="Couldn't load invoices" description="We could not load the invoices. Please try again." testid="invoices-error"
              action={<Button variant="outline" size="sm" onClick={fetchData} data-testid="invoices-retry">Retry</Button>} />
          ) : isEmpty ? (
            <EmptyState icon={Receipt} title="No invoices found" description="No invoices found for the selected period and filters." testid="invoices-empty"
              action={hasActiveFilters ? <Button variant="outline" size="sm" onClick={clearAllFilters} data-testid="invoices-empty-clear">Clear filters</Button> : undefined} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Participant</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map(inv => (
                  <TableRow key={inv.id} className="hover:bg-muted/60">
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <span>{inv.invoice_number}</span>
                        {inv.is_split_billed && (
                          <span data-testid={`invoice-split-badge-${inv.id}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200"
                            title={`Split-billed · ${inv.auto_split_summary?.lines || inv.line_items?.length || ''} allocation lines`}>
                            Split-billed{inv.source_orchestrator === 'phase3f_auto' ? ' (auto)' : ''}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{inv.participant_name || 'N/A'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtAU(inv.period_start)} – {fmtAU(inv.period_end)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-medium">{money(inv.total_amount)}</TableCell>
                    <TableCell><StatusBadge status={inv.status || 'GENERATED'} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <IconButton label="View Invoice" testid={`invoice-view-${inv.id}`} onClick={() => viewDetail(inv)}><Eye className="h-3.5 w-3.5" /></IconButton>
                        <IconButton label="Download Invoice" testid={`invoice-download-${inv.id}`} onClick={() => downloadRowPDF(inv.id)}><Download className="h-3.5 w-3.5" /></IconButton>
                        {canVoid && String(inv.status).toUpperCase() !== 'VOIDED' && (
                          <IconButton label="Void Invoice" testid={`invoice-void-${inv.id}`} onClick={() => { setVoidTarget(inv); setVoidReason(''); }}><Ban className="h-3.5 w-3.5 text-destructive" /></IconButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!listLoading && !loadError && total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="invoices-pagination">
          <div className="text-sm text-muted-foreground" data-testid="invoices-range-count">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Per page</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20" data-testid="invoices-page-size" aria-label="Rows per page"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} data-testid="invoices-prev-page" aria-label="Previous page">Previous</Button>
              <span className="text-sm tabular-nums whitespace-nowrap" data-testid="invoices-page-indicator" aria-live="polite">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="invoices-next-page" aria-label="Next page">Next</Button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Confirmation */}
      <ConfirmDialog open={generateConfirm} onOpenChange={setGenerateConfirm} title="Generate Invoice"
        description={`Generate invoice for ${participants.find(p => p.id === form.participant_id)?.first_name || 'participant'} from ${form.period_start} to ${form.period_end}? This will mark included timesheets as INVOICED.`}
        confirmLabel="Generate Invoice" loading={loading} onConfirm={handleGenerate} />

      {/* Invoice Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Invoice {detailDialog?.invoice_number}</DialogTitle><DialogDescription>Invoice details and line items</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Participant:</span> <span className="font-medium">{detailDialog?.participant_name}</span></div>
              <div><span className="text-muted-foreground">NDIS:</span> <span className="font-mono">{detailDialog?.ndis_number}</span></div>
              <div><span className="text-muted-foreground">Period:</span> {fmtAU(detailDialog?.period_start)} – {fmtAU(detailDialog?.period_end)}</div>
              <div><span className="text-muted-foreground">Total:</span> <span className="font-bold text-lg">{money(detailDialog?.total_amount)}</span></div>
            </div>
            {detailDialog?.bill_to && (
              <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900" data-testid="invoice-bill-to">
                <span className="font-semibold">Bill to ({detailDialog.bill_to.relationship || detailDialog.bill_to.party})</span>
                {' · '}{detailDialog.funding_method || 'UNRESOLVED'}
                <div className="mt-1">{[detailDialog.bill_to.organisation, detailDialog.bill_to.name].filter(Boolean).join(' — ') || '—'}</div>
                <div className="text-sky-700">{[detailDialog.bill_to.email, detailDialog.bill_to.phone, detailDialog.bill_to.address].filter(Boolean).join(' · ')}</div>
              </div>
            )}
            {(detailDialog?.funding_routing_blocked || []).length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="invoice-routing-blocked">
                <span className="font-semibold">{detailDialog.funding_routing_blocked.length} timesheet(s) left out by funding routing</span>
                <ul className="mt-1 list-disc pl-4">
                  {detailDialog.funding_routing_blocked.slice(0, 5).map((b, i) => <li key={i}>{b.date}: {b.error} — {b.message}</li>)}
                </ul>
              </div>
            )}
            {detailDialog?.is_split_billed && (
              <div className="rounded-md border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900" data-testid="invoice-split-billed-banner">
                <span className="font-semibold">Split-billed invoice</span>{' · '}
                {detailDialog?._allocation_lines?.totals?.line_count ?? (detailDialog?.line_items || []).length} allocation
                {(detailDialog?._allocation_lines?.totals?.line_count ?? (detailDialog?.line_items || []).length) === 1 ? '' : 's'}
                {' · GST '}{money(detailDialog?._allocation_lines?.totals?.gst ?? 0)}
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow><TableHead>Date</TableHead><TableHead>Service</TableHead><TableHead>Staff</TableHead><TableHead className="text-right">Time (units)</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Amount</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {(detailDialog?.line_items || []).map((item, i) => (
                  <TableRow key={i} data-testid={item.allocation_id ? `inv-alloc-line-${item.allocation_id}` : `inv-line-${i}`}>
                    <TableCell className="font-mono text-xs">{item.date}</TableCell>
                    <TableCell>{item.service_name}
                      {item.allocation_id && (
                        <div className="mt-0.5 text-[10px] text-violet-700 font-mono">
                          {item.support_item_number || '—'}
                          {item.funding_plan_id ? ` · ${String(item.funding_plan_id).slice(0, 8)}` : ''}
                          {item.gst_treatment === 'GST_APPLICABLE' ? ` · +${money(item.gst_amount || 0)} GST` : ''}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{item.staff_name}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap" data-testid={`inv-line-qty-${i}`}>{formatInvoiceQuantity(item)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(item.rate)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{money(item.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Void Invoice — reason required + confirmation with amount & restoration */}
      <Dialog open={!!voidTarget} onOpenChange={(o) => { if (!o && !voidProcessing) { setVoidTarget(null); setVoidReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="invoice-void-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ban className="h-4 w-4 text-destructive" /> Void Invoice {voidTarget?.invoice_number}</DialogTitle>
            <DialogDescription>This reverses the invoice and restores its funding. The invoice and its line items are preserved for audit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Invoice amount</span><span className="font-bold" data-testid="invoice-void-amount">{money(voidTarget?.total_amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Funding to be restored</span><span className="font-bold text-green-700" data-testid="invoice-void-restore">{money(voidTarget?.total_amount)}</span></div>
            </div>
            <div>
              <Label htmlFor="void-reason">Reason <span className="text-destructive">*</span></Label>
              <Input id="void-reason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g. Incorrect hours billed" disabled={voidProcessing} data-testid="invoice-void-reason" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setVoidTarget(null); setVoidReason(''); }} disabled={voidProcessing} data-testid="invoice-void-cancel">Cancel</Button>
              <Button variant="destructive" onClick={handleVoidInvoice} disabled={!voidReason.trim() || voidProcessing} data-testid="invoice-void-confirm">
                {voidProcessing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Voiding…</> : 'Confirm Void'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
